// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { type Control, type FieldErrors, Controller, useWatch } from 'react-hook-form';
import { TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { ReactNode } from 'react';
import SectionHeading from './SectionHeading';
import FieldLabel from './FieldLabel';
import LabeledDatePicker from './LabeledDatePicker';
import { GRID_SPACING, COL_2 } from '../constants/layout';
import FormSection from './FormSection';
import { PATIENT_DETAILS_FIELDS, type FormFieldSetting } from '../constants/encounterFieldsConfig';
import { hasFieldValue } from '../utils/hasFieldValue';

export type PatientDetailsFieldKey = (typeof PATIENT_DETAILS_FIELDS)[number];

const DEFAULT_SETTING: FormFieldSetting = { visible: true, required: true };

interface PatientDetailsSectionProps {
  /** Any react-hook-form Control — accepted as unknown to avoid generic invariance. */
  control: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  /**
   * Feature 043 — per-field visibility/required from practice settings, keyed the
   * same way as `EncounterFieldsConfig.encounterFields`. A field omitted from the
   * map (or an omitted `fields` prop entirely) defaults to visible+required, so
   * edit-claim's usage — which passes no `fields` — is unaffected.
   */
  fields?: Partial<Record<PatientDetailsFieldKey, FormFieldSetting>>;
  /** Optional banner rendered inside the section (e.g. shared-changes warning). */
  warning?: ReactNode;
}

/**
 * Shared "Patient Details" section (feature 034) used by both submit-claim and
 * edit-claim. Fields: First Name*, Last Name*, Date of Birth*, Insurance*, Insurance ID*.
 *
 * `resolved` is typed `Record<PatientDetailsFieldKey, FormFieldSetting>` — sourced
 * from `PATIENT_DETAILS_FIELDS` (the same list `encounterFieldsConfig.ts` uses to
 * build `ENCOUNTER_CONFIGURABLE_KEYS`), so adding a field there without adding a
 * line here is a compile error, not a silently-unrendered field.
 */
export default function PatientDetailsSection({ control, errors: errs, fields, warning }: PatientDetailsSectionProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ctrl = control as Control<any>;

  // Feature 043 — a field with a saved value stays visible even when practice
  // settings mark it not-visible (hiding real data would look like data loss).
  const watched = useWatch({ control: ctrl, name: PATIENT_DETAILS_FIELDS });
  const hasValue = (i: number) => hasFieldValue(watched[i]);

  const resolved: Record<PatientDetailsFieldKey, FormFieldSetting> = {
    firstName: fields?.firstName ?? DEFAULT_SETTING,
    lastName: fields?.lastName ?? DEFAULT_SETTING,
    dateOfBirth: fields?.dateOfBirth ?? DEFAULT_SETTING,
    medicalRecordNumber: fields?.medicalRecordNumber ?? DEFAULT_SETTING,
    insurer: fields?.insurer ?? DEFAULT_SETTING,
    memberId: fields?.memberId ?? DEFAULT_SETTING,
  };
  const rawFirstName = resolved.firstName;
  const rawLastName = resolved.lastName;
  const rawDateOfBirth = resolved.dateOfBirth;
  const rawMedicalRecordNumber = resolved.medicalRecordNumber;
  const rawInsurer = resolved.insurer;
  const rawMemberId = resolved.memberId;

  const firstName = { ...rawFirstName, visible: rawFirstName.visible || hasValue(0) };
  const lastName = { ...rawLastName, visible: rawLastName.visible || hasValue(1) };
  const dateOfBirth = { ...rawDateOfBirth, visible: rawDateOfBirth.visible || hasValue(2) };
  const medicalRecordNumber = { ...rawMedicalRecordNumber, visible: rawMedicalRecordNumber.visible || hasValue(3) };
  const insurer = { ...rawInsurer, visible: rawInsurer.visible || hasValue(4) };
  const memberId = { ...rawMemberId, visible: rawMemberId.visible || hasValue(5) };

  if (
    !firstName.visible &&
    !lastName.visible &&
    !dateOfBirth.visible &&
    !medicalRecordNumber.visible &&
    !insurer.visible &&
    !memberId.visible
  ) {
    return null;
  }

  return (
    <FormSection>
      <SectionHeading title="Patient Details" />
      {warning}
      <Grid container spacing={GRID_SPACING}>
        {firstName.visible && (
          <Grid size={COL_2}>
            <FieldLabel required={firstName.required}>First Name</FieldLabel>
            <Controller
              name="firstName"
              control={ctrl}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="First name"
                  fullWidth
                  size="small"
                  error={!!errs.firstName}
                  helperText={errs.firstName?.message as string | undefined}
                  inputProps={{ autoComplete: 'off' }}
                />
              )}
            />
          </Grid>
        )}
        {lastName.visible && (
          <Grid size={COL_2}>
            <FieldLabel required={lastName.required}>Last Name</FieldLabel>
            <Controller
              name="lastName"
              control={ctrl}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Last name"
                  fullWidth
                  size="small"
                  error={!!errs.lastName}
                  helperText={errs.lastName?.message as string | undefined}
                  inputProps={{ autoComplete: 'off' }}
                />
              )}
            />
          </Grid>
        )}
        {dateOfBirth.visible && (
          <Grid size={COL_2}>
            <Controller
              name="dateOfBirth"
              control={ctrl}
              render={({ field }) => (
                <LabeledDatePicker
                  label="Date of Birth"
                  required={dateOfBirth.required}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={!!errs.dateOfBirth}
                  helperText={errs.dateOfBirth?.message as string | undefined}
                  maxDate={new Date()}
                />
              )}
            />
          </Grid>
        )}
        {medicalRecordNumber.visible && (
          <Grid size={COL_2}>
            <FieldLabel required={medicalRecordNumber.required}>Medical Record Number (MRN)</FieldLabel>
            <Controller
              name="medicalRecordNumber"
              control={ctrl}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="MRN"
                  fullWidth
                  size="small"
                  error={!!errs.medicalRecordNumber}
                  helperText={errs.medicalRecordNumber?.message as string | undefined}
                  inputProps={{ autoComplete: 'off' }}
                />
              )}
            />
          </Grid>
        )}
        {insurer.visible && (
          <Grid size={COL_2}>
            <FieldLabel required={insurer.required}>Insurer</FieldLabel>
            <Controller
              name="insurer"
              control={ctrl}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Insurer"
                  fullWidth
                  size="small"
                  error={!!errs.insurer}
                  helperText={errs.insurer?.message as string | undefined}
                  inputProps={{ autoComplete: 'off' }}
                />
              )}
            />
          </Grid>
        )}
        {memberId.visible && (
          <Grid size={COL_2}>
            <FieldLabel required={memberId.required}>Member ID</FieldLabel>
            <Controller
              name="memberId"
              control={ctrl}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Member ID"
                  fullWidth
                  size="small"
                  error={!!errs.memberId}
                  helperText={errs.memberId?.message as string | undefined}
                  inputProps={{ autoComplete: 'off' }}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </FormSection>
  );
}
